// Supporting documents on the parcel card's appeal levels — filed as ordinary documents in the Appeals folder, so the
// card and the Documents panel show ONE stored copy (migration 2026-10-12_appeal_supporting_docs_apply).
//
//   file_exchange.appeal_level  which level the document supports (appeal_one, appeal_two, … — the card's level keys)
//   file_exchange.year_subfolder the VALUE year the card was on (Appeals is year-foldered)
//   file_exchange.notice_no     the notice whose appeal it supports (utils/notices; the original Proposed is 0)
//   folder / type               Appeals (DOC_KEYS.appealResults), a type picked from that folder
//
// The card reads by parcel + year + level in that folder; the Documents panel lists the same row under Appeals / year
// through the parcel_documents mirror. Moving the document to another folder in the Documents screens takes it off
// the card (the folder is part of the card's filter).
//
// ⛔ Upload FIRST, then the row: no file_exchange row for bytes that are not in R2 (same order as fileWithoutScanning).
import { supabase } from '../supabase';
import { buildFilingUpdates } from '../constants/docFiling';
import { DOC_KEYS } from './docVocab';
import { ORIGINAL_NOTICE } from './notices';
import { uploadFile } from './uploadFile';

export const APPEAL_DOC_ACTION = 'attach_supporting_document';
export const APPEAL_DOC_COLS = 'id, file_name, type_key, storage_path, filed_at, filed_by';

// The documents filed against one parcel / value year / notice / appeal level. → { data, error }
export async function loadAppealDocs(parcelId, year, level, notice = ORIGINAL_NOTICE) {
  const { data, error } = await supabase.from('file_exchange').select(APPEAL_DOC_COLS)
    .eq('parcel_id', parcelId).eq('year_subfolder', String(year)).eq('notice_no', notice).eq('appeal_level', level)
    .eq('folder_key', DOC_KEYS.appealResults)
    .order('filed_at', { ascending: true });
  return { data: data || [], error };
}

// → { ok:true, id, warning? } or { ok:false, error } (error says what happened; nothing written except where
// `warning` says so).
export async function fileAppealDoc({ vocab, typeKey, property, parcel, year, level, notice = ORIGINAL_NOTICE, file, staffName }) {
  const by = staffName || 'NTRG Staff';
  const y = String(year ?? '').trim();
  if (!file) return { ok: false, error: 'No file chosen.' };
  if (!/^(19|20)\d{2}$/.test(y)) return { ok: false, error: 'No value year — nothing was filed.' };
  if (!/^appeal_[a-z0-9]+$/.test(level || '')) return { ok: false, error: 'No appeal level — nothing was filed.' };
  if (!property?.id || !property.client_id) return { ok: false, error: 'This parcel has no property with a client — nothing was filed.' };
  const type = vocab?.type(typeKey);
  // The card only offers Appeals types; refuse anything else rather than file a supporting doc outside Appeals.
  if (!type || type.folder_key !== DOC_KEYS.appealResults) return { ok: false, error: 'Pick an Appeals document type — nothing was filed.' };

  let filing;
  try {
    filing = buildFilingUpdates({
      vocab, docType: type.key, clientId: property.client_id, propertyId: property.id, parcelId: parcel.id, year: y,
      viewableClient: false, viewableConsultant: true, existingPropertyId: property.id, staffName: by,
    });
  } catch (e) { return { ok: false, error: `${e.message} Nothing was filed.` }; }
  const { updates, folder, docFields } = filing;
  const acct = parcel.account_number || null;

  const fileName = file.name || `${type.name}_${y}.pdf`;
  const safeAcct = String(acct || '').replace(/[^a-zA-Z0-9_-]/g, '_');
  const storagePath = `${property.client_id}/mpid-${property.master_property_id}/${folder}/${y}/${safeAcct}_${Date.now()}_${fileName}`;
  const { error: ue } = await uploadFile(storagePath, file);
  if (ue) return { ok: false, error: `The file failed to upload — nothing was filed. ${ue.message}` };

  const { data, error } = await supabase.from('file_exchange').insert({
    ...updates, notice_no: notice, appeal_level: level,
    client_id: property.client_id, account_number: acct, master_property_id: String(property.master_property_id || ''),
    uploaded_by_name: by, direction: 'ntrg_internal', file_name: fileName, file_size_bytes: file.size || 0, storage_path: storagePath,
  }).select('id').single();
  if (error || !data?.id) return { ok: false, error: `Filing failed — ${error ? error.message : 'no row was created'}` };
  const id = data.id;

  const warnings = [];
  const { error: pdErr } = await supabase.from('parcel_documents').upsert({
    parcel_id: parcel.id, file_exchange_id: id, ...docFields, year_subfolder: y, property_id: property.id,
    master_property_id: property.master_property_id, account_number: acct,
  }, { onConflict: 'file_exchange_id' });
  if (pdErr) warnings.push(`it may not show in the parcel's Documents panel (${pdErr.message})`);

  const { error: logErr } = await supabase.from('activity_log').insert({
    staff_user: by, action: APPEAL_DOC_ACTION, entity_type: 'file_exchange', entity_id: id,
    entity_name: `${type.name} · ${property.name || ''} · ${y}`, property_name: property.name || null,
    details: { parcel_id: parcel.id, notice_no: notice, appeal_level: level, tax_year: y, type_key: type.key, file_name: fileName },
  });
  if (logErr) warnings.push(`the activity-log entry was not saved (${logErr.message})`);

  return { ok: true, id, warning: warnings.length ? `Filed, but ${warnings.join('; ')}.` : null };
}
